import { CastingBase } from "../json-schemas/base/casting"
import { CesuMileageReimbursementBase } from "../json-schemas/base/cesu_mileage_reimbursement"
import { CesuTransportFeeBase } from "../json-schemas/base/cesu_transport_fee"
import { CesuQuotationBase } from "../json-schemas/base/cesu_quotation"
import { InvoiceBase } from "../json-schemas/base/invoice"
import { ItemQuotationBase } from "../json-schemas/base/item_quotation"
import { ProposalBase } from "../json-schemas/base/proposal"
import { QuotationBase } from "../json-schemas/base/quotation"
import { BackofficeQuotationShowEditPdfResponse } from "../json-schemas/serializers/backoffice-quotation-show-edit-pdf-response"
import { BackofficeQuotationShowUpdatePdfResponse } from "../json-schemas/serializers/backoffice-quotation-show-update-pdf-response"
import { BackofficeQuotationsCountPerScopeResponse } from "../json-schemas/serializers/backoffice-quotations-count-per-scope-response"
import { BackofficeQuotationsIndexResponse } from "../json-schemas/serializers/backoffice-quotations-index-response"
import { CastingNewOrEditProps } from "../json-schemas/serializers/casting-new-or-edit-props"
import { EmployeeEventInvoiceEventSimulateResponse } from "../json-schemas/serializers/employee-event-invoice-event-simulate-response"
import { httpGet, httpGetQueryParams, httpParamsRemoveDiacriticsToSortableNames, httpPatch, httpPost, httpGetRoute, httpDelete } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import i18nScopedData from './quotations.service.yml'

export interface RansackQuery {
  scope?: 'proposed_casting_not_expired' | 'proposed_casting_expired' | 'customer_refused_or_another_chosen' | 'accepted' | 'invoiced' | 'archived' | 'all'
  'q[id_not_in][]'?: number[]
  'q[employee_id_eq]'?: number
  'q[employee_id_null]'?: boolean
  'q[employee_sortable_name_or_employee_reversed_sortable_name_cont]'?: string
  'q[customer_sortable_name_or_customer_reversed_sortable_name_cont]'?: string
}

const i18nScoped = new I18nService(i18nScopedData)

export function quotationsList (query: RansackQuery = {}): Promise<BackofficeQuotationsIndexResponse> {
  return httpGet(`/${httpGetRoute()}/quotations`, httpParamsRemoveDiacriticsToSortableNames(query))
}

export function quotationCountPerScope (query: RansackQuery = {}): Promise<BackofficeQuotationsCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/quotations/count_per_scope`, query)
}

export function quotationPreviewInvoice (id: QuotationBase['id'], body: any): Promise<EmployeeEventInvoiceEventSimulateResponse> {
  return httpPost(`/${httpGetRoute()}/quotations/${id}/preview_invoice`, { quotation: body })
}

export function quotationGenerateInvoice (id: QuotationBase['id'], body: any): Promise<{ invoice_id: InvoiceBase['id'] }> {
  return httpPost(`/${httpGetRoute()}/quotations/${id}/generate_invoice`, { quotation: body })
}

export function quotationEditPdf (id: QuotationBase['id']): Promise<BackofficeQuotationShowEditPdfResponse & { dataForCastingForm?: CastingNewOrEditProps }> {
  return httpGet(`/${httpGetRoute()}/quotations/${id}/edit_pdf`)
}

export function quotationUpdatePdf (id: QuotationBase['id'], body: any) : Promise<BackofficeQuotationShowUpdatePdfResponse> {
  return httpPatch(`/${httpGetRoute()}/quotations/${id}/update_pdf`, { quotation: body })
}

export function quotationCreate (body: any): Promise<{ quotation_id: QuotationBase['id'] }> {
  return httpPost(`/${httpGetRoute()}/quotations`, body)
}

export function quotationUpdate (id: QuotationBase['id'], body: any) {
  return httpPatch(`/${httpGetRoute()}/quotations/${id}`, body)
}

export function quotationUnsend (id: QuotationBase['id']): Promise<void> {
  return httpPatch(`/${httpGetRoute()}/quotations/${id}/unsend`, {})
}

export function quotationUploadSignedPdf (id: QuotationBase['id'], body: { prefered_proposal_id?: ProposalBase['id'], mode: 'offline_sign' | 'oral_sign', document_attributes?: { file: string } }) {
  return httpPatch(`/${httpGetRoute()}/quotations/${id}/upload_signed_pdf`, { quotation: body })
}

export function quotationDestroy (id: QuotationBase['id']): Promise<void> {
  return httpDelete(`/${httpGetRoute()}/quotations/${id}`)
}

export function quotationIndexPath () {
  return `/${httpGetRoute()}/quotations`
}

export function quotationNewPath (quotationType: QuotationBase['quotation_type']) {
  return `/${httpGetRoute()}/quotations/new${httpGetQueryParams({ quotation_type: quotationType })}`
}

export function quotationShowPath (id: QuotationBase['id']) {
  return `/${httpGetRoute()}/quotations/${id}`
}

export function quotationPrepareInvoicePath (id: QuotationBase['id']) {
  return `/${httpGetRoute()}/quotations/${id}/prepare_invoice`
}

export function quotationPreviewPath (params: any) {
  return `/${httpGetRoute()}/quotations/preview${httpGetQueryParams(params)}`
}

export function quotationI18nScope (scope: keyof BackofficeQuotationsCountPerScopeResponse['stats']) {
  return i18nScoped.t(`scope.${scope}`)
}

type ItemQuotationToSubmit = Pick<ItemQuotationBase, 'description' | 'kind' | 'price_cents' | 'quantity' | 'tax_rules_group_id' | 'verbose_description'> & { id?: ItemQuotationBase['id'], _destroy?: boolean }
type CesuMileageReimbursementToSubmit = Pick<CesuMileageReimbursementBase, 'amount_cents' | 'applyness'> & { _destroy?: boolean }
type CesuTransportFeeToSubmit = Pick<CesuTransportFeeBase, 'amount_cents' | 'applyness'> & { _destroy?: boolean }
type CesuQuotationToSubmit = Pick<CesuQuotationBase, 'management_fee_with_taxes_cents' | 'gross_employee_hourly_salary_cents'> & {
  cesu_mileage_reimbursement_attributes?: CesuMileageReimbursementToSubmit | null
  cesu_transport_fee_attributes?: CesuTransportFeeToSubmit | null
}
type QuotationToSubmit = Pick<QuotationBase, 'price_cents' | 'vat_price_cents' | 'round_on_vat_price' | 'signable'> & {
  item_quotations_attributes: ItemQuotationToSubmit[]
  cesu_quotation_attributes?: CesuQuotationToSubmit | null
}

export function quotationReadyForSubmit (quotation: QuotationToSubmit) {
  if (quotation.cesu_quotation_attributes) {
    if (quotation.cesu_quotation_attributes.management_fee_with_taxes_cents === null) return false
    if (quotation.cesu_quotation_attributes.gross_employee_hourly_salary_cents === null) return false

    const complements = [quotation.cesu_quotation_attributes.cesu_mileage_reimbursement_attributes, quotation.cesu_quotation_attributes.cesu_transport_fee_attributes]
    if (complements.some(complement => complement && !complement._destroy && (!complement.amount_cents || !complement.applyness))) return false
    return true
  }

  if (quotation.item_quotations_attributes.length) {
    for (const itemQuotation of quotation.item_quotations_attributes) {
      if (itemQuotation._destroy) continue
      if (!itemQuotation.description || !itemQuotation.kind || !itemQuotation.quantity || !itemQuotation.tax_rules_group_id) return false
      if (itemQuotation.price_cents === null) return false
    }

    return true
  }

  return !!quotation.price_cents || !!quotation.vat_price_cents
}

export function quotationOpenPreview (quotation: QuotationToSubmit & { proposal_id?: number }, castingId: CastingBase['id']) {
  const url = quotationPreviewPath({
    quotation: { ...quotation, casting_id: castingId }
  })

  window.open(url, '_blank')
}
